import { Capacitor } from '@capacitor/core';
import api, { API_BASE } from './api';
import { isBusinessApp } from './platform';

// The bundle id of the NATIVE app this build is: the backend stores it on the
// push token and uses it as the apns-topic, so a Business-app token never
// receives a client push (and vice versa). Must match the Xcode bundle ids.
const APP_BUNDLE = isBusinessApp ? 'com.mariusnpop.petzy.business' : 'com.mariusnpop.petzy';

// Centralized native-push client. One place owns: listener wiring (idempotent),
// token registration + storage, contextual permission requests, tap deep-linking,
// and logout cleanup. Consumed by usePushNotifications (register-if-granted),
// PushPrimingSheet (contextual prompt), booking flows (trigger), and AuthContext
// (logout delete). Everything is best-effort and swallowed — push must never
// break a real user flow.
//
// Apple guidance applied (see the "Petzy Notification Standard" reference):
//  • Ask for permission IN CONTEXT (after the first booking), never cold at login.
//  • A denied user can't be re-prompted — recovery is a Settings deep-link.
//  • Re-register + re-POST the token each launch; on logout, disassociate it.

const DEVICE_TOKEN_KEY = 'petzy_push_token';
const ASKED_KEY = 'petzy_push_asked';
const AUTH_TOKEN_KEY = 'token'; // matches utils/api.js request interceptor

let listenersWired = null; // null | Promise (the in-flight/settled wiring)
let navigateRef = null;
// A cold-start tap can fire before the router mounts (navigateRef still null);
// park the route here and let setPushNavigate() flush it once navigation exists.
let pendingRoute = null;

// Push runs on both native platforms: iOS via APNs, Android via FCM. The
// @capacitor/push-notifications API is identical across them (register →
// 'registration' token → backend), and the backend routes each token to APNs or
// FCM by the `platform` it was stored under. Web has no native push → skip.
function isPushNative() {
  const p = Capacitor.getPlatform();
  return p === 'ios' || p === 'android';
}

// Route a tapped notification to the screen it's about (data.type is set by the
// backend on every push). Falls back to My Bookings for anything booking-shaped.
function routeForData(data = {}) {
  // Business app: every push is a STAFF alert about the salon's own bookings.
  // A new request / cancellation / reschedule deep-links straight to THAT
  // booking (SalonAppointmentsPage reads ?bookingId=X and opens its modal); a
  // new review opens the reviews screen.
  if (isBusinessApp) {
    if (data.type === 'staff_review') return '/salon/reviews';
    // Onboarding nudge → the dashboard, where the setup checklist lives.
    if (data.type === 'staff_onboarding') return '/salon/dashboard';
    // WhatsApp-budget alert → the message-pack storefront.
    if (data.type === 'staff_wa_budget') return '/salon/billing#mesaje-whatsapp';
    return data.bookingId
      ? `/salon/appointments?bookingId=${encodeURIComponent(data.bookingId)}`
      : '/salon/appointments';
  }
  switch (data.type) {
    case 'waitlist_available':
    case 'reengagement':
      return data.salonSlug ? `/salon/${data.salonSlug}` : '/';
    case 'booking_recovery':
      // The push carries the DRAFT id — resume the unfinished booking through
      // the same /booking/continue flow the recovery email links to (it
      // rehydrates the drawer at the deepest completed step). My Bookings has
      // nothing to show for a draft.
      return data.draftId ? `/booking/continue/${encodeURIComponent(data.draftId)}` : '/';
    case 'review_request':
      // Canonical review deep-link (matches the email/WhatsApp CTAs): the
      // salon page auto-opens the review modal via ?review=1. Older pushes
      // lack the slug — land on the tab where the COMPLETED booking lives,
      // which has its own "Recenzie" button.
      return data.salonSlug
        ? `/salon/${data.salonSlug}?review=1`
        : '/my-bookings?tab=past';
    case 'booking_status':
      // A cancelled booking is filed under the "Anulate" tab — the default
      // "Viitoare" tab would show nothing about it.
      return data.status === 'CANCELLED' ? '/my-bookings?tab=cancelled' : '/my-bookings';
    default:
      // booking_reminder, booking_reschedule… all upcoming → default tab.
      return '/my-bookings';
  }
}

async function wireListeners() {
  // Memoize the wiring as a PROMISE, not a boolean. registerIfGranted() at launch
  // and requestPushPermission() from the priming sheet each do
  // `await wireListeners(); await PushNotifications.register();`, so two can race.
  // A boolean claimed before the first await stops DUPLICATE listeners, but lets
  // the SECOND caller pass the guard and return before the listeners are actually
  // attached — it would then call register() and could miss the 'registration'
  // token event. Storing the in-flight promise makes every concurrent caller
  // await the SAME wiring, so nobody reaches register() until the listeners are
  // up (and still exactly one set is attached). On failure, reset to null so
  // register()'s next-launch retry can re-wire (transient import errors self-heal).
  if (listenersWired) {
    await listenersWired;
    return;
  }
  listenersWired = (async () => {
    const { PushNotifications } = await import('@capacitor/push-notifications');
    await PushNotifications.addListener('registration', (token) => {
      try { localStorage.setItem(DEVICE_TOKEN_KEY, token.value); } catch { /* noop */ }
      // platform drives backend routing: 'ios' → APNs, 'android' → FCM. On iOS
      // token.value is the APNs device token; on Android it's the FCM token —
      // same event, the plugin abstracts the difference.
      api.post('/users/me/push-token', {
        token: token.value,
        platform: Capacitor.getPlatform(),
        appBundle: APP_BUNDLE,
      }).catch(() => {});
    });
    await PushNotifications.addListener('registrationError', (err) => {
      // Surface APNs/FCM registration failures (missing entitlement, bad
      // provisioning, no google-services.json, no network) — otherwise the token
      // silently never arrives and there's nothing to diagnose. register() re-runs
      // each launch, so a transient failure self-heals next time.
      console.warn('[push] push registration failed:', err?.error || err);
    });
    await PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      const data = action?.notification?.data || {};
      const route = routeForData(data);
      if (navigateRef) {
        try { navigateRef(route); } catch { /* noop */ }
      } else {
        pendingRoute = route;
      }
    });
  })();
  try {
    await listenersWired;
  } catch (e) {
    listenersWired = null; // reset so the next call re-attempts wiring
    throw e;
  }
}

// Called by the hook so the tap handler can navigate with the live router.
export function setPushNavigate(navigate) {
  navigateRef = navigate;
  if (navigate && pendingRoute) {
    const route = pendingRoute;
    pendingRoute = null;
    try { navigate(route); } catch { /* noop */ }
  }
}

// Silent path: if permission is ALREADY granted, refresh + POST the token. Never
// prompts — that's the whole point (Apple: don't ask cold). Safe on every launch.
export async function registerIfGranted() {
  if (!isPushNative()) return;
  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');
    const perm = await PushNotifications.checkPermissions();
    if (perm.receive !== 'granted') return;
    await wireListeners();
    await PushNotifications.register();
  } catch { /* plugin unavailable / not native */ }
}

export function hasAskedForPush() {
  try { return localStorage.getItem(ASKED_KEY) === '1'; } catch { return false; }
}
export function markAskedForPush() {
  try { localStorage.setItem(ASKED_KEY, '1'); } catch { /* noop */ }
}

// Should we show the priming sheet? Only on iOS, only if we've never asked, and
// only while the OS status is still 'prompt' (asking is possible). A denied or
// granted user is never primed.
export async function shouldPrimeForPush() {
  if (!isPushNative() || hasAskedForPush()) return false;
  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');
    const perm = await PushNotifications.checkPermissions();
    return perm.receive === 'prompt' || perm.receive === 'prompt-with-rationale';
  } catch { return false; }
}

// The contextual request, fired from the priming sheet's primary button. Marks
// "asked" so we never nag again. Returns 'granted' | 'denied' | 'unavailable'.
export async function requestPushPermission() {
  markAskedForPush();
  if (!isPushNative()) return 'unavailable';
  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');
    let perm = await PushNotifications.checkPermissions();
    if (perm.receive === 'prompt' || perm.receive === 'prompt-with-rationale') {
      perm = await PushNotifications.requestPermissions();
    }
    if (perm.receive === 'granted') {
      await wireListeners();
      await PushNotifications.register();
      return 'granted';
    }
    return 'denied';
  } catch { return 'unavailable'; }
}

// Business app ONLY: the salon owner/staff installed a professional dashboard
// specifically to manage bookings, so alerts are the expected default — Apple's
// contextual-priming pattern (ask after a first purchase) doesn't apply. Ask
// once, right after login, while the OS status is still promptable. No-op in the
// client app, if already asked, or once permission is decided. Idempotent.
export async function primeBusinessPushOnLogin() {
  if (!isBusinessApp || !isPushNative() || hasAskedForPush()) return;
  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');
    const perm = await PushNotifications.checkPermissions();
    if (perm.receive === 'prompt' || perm.receive === 'prompt-with-rationale') {
      await requestPushPermission();
    }
  } catch { /* plugin unavailable / not native */ }
}

// Fire from a booking-success handler. Decoupled via a window event so booking
// components don't need to import the sheet; PushPrimingSheet listens for it.
export function triggerPushPrompt() {
  if (!isPushNative()) return;
  try { window.dispatchEvent(new Event('petzy:push-prompt')); } catch { /* noop */ }
}

// Logout: disassociate this device's token from the user so the next person on
// the phone doesn't inherit their alerts. Reads the auth JWT SYNCHRONOUSLY and
// sends it explicitly, because logout clears it right after this call and the
// axios interceptor would otherwise send an unauthenticated (401) delete.
export function deleteStoredPushToken() {
  let deviceToken = null;
  let auth = null;
  try { deviceToken = localStorage.getItem(DEVICE_TOKEN_KEY); } catch { /* noop */ }
  try { auth = localStorage.getItem(AUTH_TOKEN_KEY); } catch { /* noop */ }
  try { localStorage.removeItem(DEVICE_TOKEN_KEY); } catch { /* noop */ }
  if (!deviceToken || !auth) return;
  // Raw fetch, NOT the axios `api` instance. A logout commonly fires with an
  // already-stale JWT; if this DELETE 401s, api's global response interceptor
  // would clearSessionStorage() + window.location.href='/login', hijacking the
  // logout flow's own navigation (the user asked to sign out, not to land on the
  // login screen). fetch bypasses the interceptors. The route needs only the
  // Authorization header + a JSON { token } body — no Turnstile/native header.
  // Best-effort — the next user's register() reassigns the token anyway.
  fetch(`${API_BASE}/users/me/push-token`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${auth}`,
    },
    body: JSON.stringify({ token: deviceToken }),
  }).catch(() => { /* best-effort */ });
}
